<script>
export default {
  name: "MaintenanceNotice",
  data() {
    return {
      primaryButtonHover: false,
      isDestroying: false
    };
  },
  mounted() {
    this.openNotice();
  },
  beforeDestroy() {
    this.isDestroying = true;
    const notice = this.$refs.notice;
    if (!notice) return;
    if (notice.open) notice.close();
    if (notice.parentNode) notice.parentNode.removeChild(notice);
  },
  methods: {
    openNotice() {
      this.$nextTick(() => {
        const notice = this.$refs.notice;
        if (this.isDestroying || !notice) return;
        // Keep Vue's root in place and move only the dialog after mounting finishes.
        if (notice.parentNode !== document.body) {
          document.body.appendChild(notice);
        }
        if (!notice.open) {
          notice.showModal();
        }
      });
    }
  }
};
</script>

<template>
  <div>
  <dialog
    ref="notice"
    class="maintenance-notice"
    aria-labelledby="maintenance-title"
    aria-describedby="maintenance-description"
    :style="{
      position: 'fixed',
      inset: '0',
      boxSizing: 'border-box',
      width: 'min(500px, calc(100vw - 32px))',
      maxWidth: 'none',
      maxHeight: 'calc(100dvh - 32px)',
      margin: 'auto',
      padding: '0',
      overflowX: 'hidden',
      overflowY: 'auto',
      border: 'none',
      borderRadius: '16px',
      backgroundColor: '#ffffff',
      color: '#1e293b',
      fontFamily: 'inherit',
      boxShadow: '0 24px 70px rgba(15, 23, 42, 0.25)',
      opacity: '1',
    }"
    @cancel.prevent
    @close="openNotice"
  >
    <!-- Top Accent -->
    <div
      :style="{
        height: '5px',
        background: 'linear-gradient(90deg, #3848CB, #8792F0)',
      }"
    ></div>
    <div
      :style="{
        padding: 'clamp(20px, 5vw, 30px)',
      }"
    >
      <!-- Header -->
      <div
        :style="{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '24px',
        }"
      >
        <!-- Migration Icon -->
        <div
          :style="{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '58px',
            height: '58px',
            flexShrink: '0',
            borderRadius: '15px',
            backgroundColor: '#EEF0FF',
            color: '#3848CB',
          }"
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 7h14" />
            <path d="m17 3 4 4-4 4" />
            <path d="M17 17H3" />
            <path d="m7 13-4 4 4 4" />
          </svg>
        </div>

      </div>
      <!-- Title -->
      <div :style="{ marginBottom: '24px' }">
        <h2
          id="maintenance-title"
          :style="{
            margin: '0 0 12px',
            padding: '0',
            color: '#0f172a',
            fontSize: '22px',
            fontWeight: '700',
            lineHeight: '1.4',
            letterSpacing: '-0.3px',
          }"
        >
          We're Moving to a New Meeting Room Booking Portal.
        </h2>
        <p
          :style="{
            margin: '0',
            padding: '0',
            color: '#64748b',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '1.8',
          }"
        >
          We are migrating to a new meeting room booking portal. Please use the new
          portal to book and manage your meeting rooms.
        </p>
      </div>
      <!-- Announcement -->
      <div
        id="maintenance-description"
        :style="{
          padding: '20px',
          border: '1px solid #C8CEFA',
          borderRadius: '12px',
          backgroundColor: '#EEF0FF',
        }"
      >
        <div
          :style="{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '12px',
          }"
        >
          <svg
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2938AC"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
            :style="{ flexShrink: '0' }"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          <span
            :style="{
              color: '#2938AC',
              fontSize: '14px',
              fontWeight: '700',
              lineHeight: '1.5',
            }"
          >
            Meeting Room Booking Portal Migration Notice
          </span>
        </div>
        <p
          :style="{
            margin: '0',
            padding: '0',
            color: '#475569',
            fontSize: '13px',
            fontWeight: '400',
            lineHeight: '1.85',
          }"
        >
          Kindly note that the current meeting room booking portal has been discontinued
          and will be shut down soon. Please go to meeting.ihextar.com for all meeting room
          bookings moving forward.
        </p>
      </div>
      <!-- Footer -->
      <div
        :style="{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: '12px',
          marginTop: '28px',
          paddingTop: '24px',
          borderTop: '1px solid #e2e8f0',
        }"
      >
        <!-- New Portal Button -->
        <a
          href="https://meeting.ihextar.com/"
          target="_blank"
          rel="noopener noreferrer"
          :style="{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '9px',
            boxSizing: 'border-box',
            minHeight: '44px',
            margin: '0',
            padding: '11px 20px',
            border: '1px solid',
            borderColor: primaryButtonHover ? '#2938AC' : '#3848CB',
            borderRadius: '9px',
            backgroundColor: primaryButtonHover ? '#2938AC' : '#3848CB',
            color: '#ffffff',
            fontFamily: 'inherit',
            fontSize: '14px',
            fontWeight: '600',
            lineHeight: '20px',
            textAlign: 'center',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            boxShadow: primaryButtonHover
              ? '0 6px 16px rgba(56, 72, 203, 0.30)'
              : '0 3px 8px rgba(56, 72, 203, 0.18)',
            transform: primaryButtonHover
              ? 'translateY(-1px)'
              : 'translateY(0)',
            transition: 'all 0.2s ease'
          }"
          @mouseenter="primaryButtonHover = true"
          @focus="primaryButtonHover = true"
          @mouseleave="primaryButtonHover = false"
          @blur="primaryButtonHover = false"
        >
          <span>Go to New Portal</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 17 17 7" />
            <path d="M8 7h9v9" />
          </svg>
        </a>
      </div>
    </div>
  </dialog>
  </div>
</template>

<style>
.maintenance-notice::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
